'use client'
import { cn } from '@/lib/utils'
import { AllPurposeLink } from '@components/all-purpose-link'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { NavItem } from '@components/nav/constants'
import {
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  navigationMenuTriggerStyle
} from '@components/navigation-menu'

import { ExcessSettlementFundsClaimModal } from '@components/excess-settlement-funds-claim-modal'
import { useUserHasClaimableExcessSettlementFunds } from '@components/excess-settlement-funds-manager-context/hooks'
import { useFundWallet } from '@privy-io/react-auth'
import { NavigationMenuTrigger } from '@radix-ui/react-navigation-menu'
import { CoinsIcon, UserIcon } from 'design-configuration/components'

import { useSession } from 'next-auth/react'
import React, { useCallback, useState } from 'react'
import { useAuth } from '../artblocks-auth-provider'
import { userProfileRoute } from '@utils/routes'
import { buttonVariants } from '@components/button'
import { useProfilePicture } from '@/hooks/use-profile-picture'

export default function AccountNavigationMenuItem(): React.ReactElement {
  const { logout, status } = useAuth()
  const { data: session } = useSession()
  const { profilePictureUrl } = useProfilePicture()
  const { fundWallet } = useFundWallet()

  const [showExcessSettlementFundsClaimModal, setShowExcessSettlementFundsClaimModal] = useState(false)
  const hasClaimableExcessSettlementFunds = useUserHasClaimableExcessSettlementFunds()

  const accountSubItems = getAccountSubItems({
    walletAddress: session?.user?.publicAddress,
    onDisconnect: () => {
      logout()
    },
    fundWallet,
    hasClaimableExcessSettlementFunds,
    onClaimExcessSettlementFunds: () => {
      setShowExcessSettlementFundsClaimModal(true)
    }
  })

  const handleCloseExcessSettlementFundsClaimModal = useCallback(() => {
    setShowExcessSettlementFundsClaimModal(false)
  }, [])

  const ProfileIcon = () => {
    return (
      <>
        {profilePictureUrl ? (
          <div className="w-md h-md border-on-surface relative overflow-hidden rounded-full border">
            <ImageWithCustomLoader alt="Profile Picture" fill src={profilePictureUrl} />
          </div>
        ) : (
          <UserIcon className={`fill-on-surface w-sm h-sm`} />
        )}
      </>
    )
  }

  const ProfileIconWithNavigation = () => {
    return (
      <AllPurposeLink href={`${session?.user?.publicAddress ? userProfileRoute(session?.user?.publicAddress) : ''}`}>
        <ProfileIcon />
      </AllPurposeLink>
    )
  }

  return (
    <NavigationMenuItem value="account">
      <div className="relative">
        {(() => {
          if (status === 'loading' || status === 'authenticating') {
            return (
              <div
                className={cn(
                  navigationMenuTriggerStyle(),
                  'user-select-none w-sm h-sm border-on-surface pointer-events-none flex animate-pulse items-center justify-center rounded-full border'
                )}
              />
            )
          }

          if (status === 'unauthenticated') {
            return (
              <>
                <AllPurposeLink
                  href={'/login'}
                  className={cn(navigationMenuTriggerStyle(), 'flex items-center justify-center lg:hidden')}
                >
                  <UserIcon className={`fill-on-surface w-sm h-sm`} />
                </AllPurposeLink>
                <AllPurposeLink
                  href={'/login'}
                  className={cn(
                    buttonVariants({
                      colorTheme: 'outline'
                    }),
                    'py-xs px-sm hidden lg:block'
                  )}
                >
                  Sign in
                </AllPurposeLink>
              </>
            )
          }

          return (
            <>
              <NavigationMenuTrigger className={cn(navigationMenuTriggerStyle(), 'flex items-center justify-center')}>
                {/* On mobile don't make the profile icon link to profile page */}
                <div className="lg:hidden">
                  <ProfileIcon />
                </div>
                {/* On larger screens make the profile icon link to profile page */}
                <div className="hidden lg:block">
                  <ProfileIconWithNavigation />
                </div>
              </NavigationMenuTrigger>
              <NavigationMenuContent contentFor="account" align="end">
                {accountSubItems?.map((item) => (
                  <NavigationMenuLink
                    key={item.value}
                    href={item.href ?? ''}
                    className={'block min-w-full'}
                    asChild
                    onSelect={item.disableDismiss ? (e) => e.preventDefault() : undefined}
                  >
                    {item.onClick ? (
                      <button className="w-full text-left" onClick={item.onClick}>
                        {item.label}
                      </button>
                    ) : (
                      <AllPurposeLink href={item.href ?? ''}>{item.label}</AllPurposeLink>
                    )}
                  </NavigationMenuLink>
                ))}
              </NavigationMenuContent>
            </>
          )
        })()}
      </div>
      <ExcessSettlementFundsClaimModal
        show={showExcessSettlementFundsClaimModal}
        onClose={handleCloseExcessSettlementFundsClaimModal}
      />
    </NavigationMenuItem>
  )
}

const getAccountSubItems = ({
  walletAddress,
  onDisconnect,
  fundWallet,
  hasClaimableExcessSettlementFunds,
  onClaimExcessSettlementFunds
}: {
  walletAddress?: string
  onDisconnect: () => void
  fundWallet: (walletAddress: string) => void
  hasClaimableExcessSettlementFunds: boolean
  onClaimExcessSettlementFunds: () => void
}): Array<NavItem> | undefined => {
  if (!walletAddress) {
    return undefined
  }

  const items: Array<NavItem> = [
    {
      label: 'Profile',
      value: 'profile',
      href: userProfileRoute(walletAddress)
    },
    {
      label: 'Sets',
      value: 'sets',
      href: `${userProfileRoute(walletAddress)}/sets`
    },
    {
      label: 'Achievements',
      value: 'achievements',
      href: `${userProfileRoute(walletAddress)}/achievements`
    },
    {
      label: 'Manage account',
      value: 'manage-account',
      href: `/profile/settings`
    },
    {
      label: 'Add funds to wallet',
      value: 'fund-wallet',
      onClick: () => fundWallet(walletAddress)
    }
  ]

  if (hasClaimableExcessSettlementFunds) {
    items.unshift({
      label: (
        <span className="flex items-center gap-1">
          <span>Claim funds</span>
          <CoinsIcon className="w-sm h-sm fill-on-surface" />
        </span>
      ),
      value: 'claim-funds',
      onClick: onClaimExcessSettlementFunds
    })
  }

  items.push({
    label: 'Sign out',
    value: 'sign-out',
    onClick: onDisconnect
  })

  return items
}
